import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Card, useToast } from '@kss/ui';
import {
  branchService,
  restaurantTableService,
  type RestaurantTable,
} from '@kss/api';
import {
  Printer, RefreshCw, Download, QrCode as QrIcon, Eye, Plus, X, ExternalLink,
} from 'lucide-react';
import QRCode from 'qrcode';

interface AuthState {
  session?: { tenantCode?: string; tenantId?: string } | null;
}

/**
 * Print-ready QR-codes-per-table page.
 *
 *  - Loads every active restaurant table.
 *  - For each table, auto-fills a stable `qrToken` if missing
 *    (UUID v4) and persists it back through the CRUD endpoint so
 *    the same QR keeps working tomorrow.
 *  - Renders one card per table with a 240px QR pointing at
 *    `/dine/<tenantCode>/t/<qrToken>`.
 *  - "Print" opens the browser print dialog with a print stylesheet
 *    that strips chrome and lays out the cards 3-per-row on A4.
 *  - Each card has a "Download PNG" button for quick sticker
 *    printing on a label printer.
 *
 * Hosts the dine-in landing URL via `import.meta.env.VITE_DINE_HOST`
 * when set; falls back to `window.location.origin` so it Just Works
 * from any deploy.
 */

interface CardRow {
  id: string;
  code: string;
  name?: string;
  qrToken: string;
  url: string;
  pngDataUrl: string;
}

export default function TableQrCodes() {
  const toast = useToast();
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [cards, setCards] = useState<CardRow[]>([]);
  const [tenantCode, setTenantCode] = useState<string>(() => readTenantCode());
  const [dineHost, setDineHost] = useState<string>(() => readDineHost());
  const [size, setSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<CardRow | null>(null);
  const [generator, setGenerator] = useState<boolean>(false);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      const rows = await restaurantTableService.all();
      const active = (rows as RestaurantTable[]).filter((t) => t.isActive !== false);
      setTables(active);
      setSelectedIds(new Set(active.map((t) => String(t.restaurantTableId ?? ''))));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load tables.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Whenever tables / tenantCode / host change, regenerate QR PNGs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (tables.length === 0) {
        setCards([]);
        return;
      }
      const next: CardRow[] = [];
      const toPersist: RestaurantTable[] = [];
      for (const t of tables) {
        const id = String(t.restaurantTableId ?? '');
        // The live API silently drops the speculative `qrToken` field
        // (only the base 8 columns round-trip). Cache the token in
        // BOTH the speculative field AND inside `remarks` as
        // `qr=<uuid>` so the customer-side resolver can find it.
        let token = t.qrToken ?? readTokenFromRemarks(t.remarks);
        if (!token) {
          token = makeUuid();
          const newRemarks = stampToken(t.remarks, token);
          toPersist.push({ ...t, qrToken: token, remarks: newRemarks });
        } else if (!/qr=/.test(t.remarks ?? '')) {
          // Token was in `qrToken` from a previous run but never made
          // it into remarks — back-fill so customer-side can find it.
          toPersist.push({ ...t, qrToken: token, remarks: stampToken(t.remarks, token) });
        }
        const url = buildDineUrl(dineHost, tenantCode, token);
        const png = await QRCode.toDataURL(url, {
          width: 600,
          errorCorrectionLevel: 'M',
          margin: 1,
          color: { dark: '#000000ff', light: '#ffffffff' },
        });
        next.push({
          id,
          code: t.code ?? t.name ?? id,
          name: t.name,
          qrToken: token,
          url,
          pngDataUrl: png,
        });
      }
      if (cancelled) return;
      setCards(next);
      // Persist any auto-generated tokens so the QR is stable.
      if (toPersist.length > 0) {
        try {
          await restaurantTableService.update(toPersist);
          toast.success('QR tokens saved', `${toPersist.length} table(s) updated.`);
        } catch (e) {
          toast.error('Could not save tokens', e instanceof Error ? e.message : 'Try again');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tables, tenantCode, dineHost]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(
    () => cards.filter((c) => selectedIds.has(c.id)),
    [cards, selectedIds],
  );

  function toggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelectedIds(
      selectedIds.size === cards.length ? new Set() : new Set(cards.map((c) => c.id)),
    );
  }
  function downloadOne(c: CardRow) {
    const a = document.createElement('a');
    a.href = c.pngDataUrl;
    a.download = `qr-${c.code}.png`;
    a.click();
  }
  async function rotate(id: string) {
    const t = tables.find((x) => String(x.restaurantTableId ?? '') === id);
    if (!t) return;
    setBusy(true);
    try {
      const newToken = makeUuid();
      // Same belt-and-braces persistence: write the new token to BOTH
      // the speculative field and the always-persisted remarks slot.
      const fresh = { ...t, qrToken: newToken, remarks: stampToken(t.remarks, newToken) };
      await restaurantTableService.update([fresh]);
      await load();
      toast.success('QR rotated', `${t.code ?? t.name} now uses a fresh code. Reprint the sticker.`);
    } catch (e) {
      toast.error('Rotate failed', e instanceof Error ? e.message : 'Try again');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col">
      {/* Toolbar — hidden when printing */}
      <header className="no-print flex flex-wrap items-center gap-3 border-b border-border bg-surface-card px-4 py-3">
        <QrIcon className="text-orange-500" size={22} />
        <div className="flex-1 min-w-[220px]">
          <h1 className="text-base font-semibold text-content-primary">Table QR codes</h1>
          <p className="text-[11px] text-content-muted">
            One QR per table. Customers scan → dine-in menu opens.
            {selectedIds.size} of {cards.length} selected to print.
          </p>
        </div>

        <label className="flex items-center gap-1.5 text-[11px] text-content-secondary">
          Tenant code
          <input
            value={tenantCode}
            onChange={(e) => setTenantCode(e.target.value.trim())}
            placeholder="bill"
            className="h-8 w-20 rounded-md border border-border bg-surface-card px-2 text-xs"
          />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-content-secondary">
          Public host
          <input
            value={dineHost}
            onChange={(e) => setDineHost(e.target.value.trim())}
            placeholder={typeof window === 'undefined' ? '' : window.location.origin}
            className="h-8 w-56 rounded-md border border-border bg-surface-card px-2 text-xs"
          />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-content-secondary">
          Size
          <select
            value={size}
            onChange={(e) => setSize(e.target.value as typeof size)}
            className="h-8 rounded-md border border-border bg-surface-card px-2 text-xs"
          >
            <option value="small">Small (6/row)</option>
            <option value="medium">Medium (3/row)</option>
            <option value="large">Large (2/row)</option>
          </select>
        </label>

        <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={() => setGenerator(true)}>
          <Plus className="h-3.5 w-3.5" /> Generate QR
        </Button>
        <Button size="sm" onClick={() => window.print()} disabled={visible.length === 0}>
          <Printer className="h-3.5 w-3.5" /> Print {visible.length || ''}
        </Button>
      </header>

      {err && (
        <Alert variant="danger" className="no-print m-3">
          {err}
        </Alert>
      )}

      {/* Selection strip — also hidden in print */}
      <div className="no-print flex items-center gap-3 border-b border-border bg-surface-card px-4 py-2 text-xs">
        <label className="inline-flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={cards.length > 0 && selectedIds.size === cards.length}
            onChange={toggleAll}
          />
          Select all
        </label>
        <span className="text-content-muted">
          The print preview only includes the selected tables.
        </span>
      </div>

      {/* Card grid — this is what prints */}
      <div className={`flex-1 overflow-y-auto bg-white p-4 ${gridClass(size)}`}>
        {cards.length === 0 && !busy ? (
          <div className="no-print col-span-full grid place-items-center py-20 text-center text-sm text-content-muted">
            <QrIcon className="mb-2" size={28} />
            No tables yet. Add some under <span className="mx-1 underline">Restaurant Tables</span> then come back.
          </div>
        ) : (
          cards.map((c) => (
            <QrCard
              key={c.id}
              card={c}
              size={size}
              selected={selectedIds.has(c.id)}
              tenantName={tenantCode}
              onToggle={() => toggle(c.id)}
              onDownload={() => downloadOne(c)}
              onRotate={() => void rotate(c.id)}
              onPreview={() => setPreview(c)}
            />
          ))
        )}
      </div>

      {preview && <PreviewModal card={preview} onClose={() => setPreview(null)} />}
      {generator && (
        <GenerateQrModal
          tenantCode={tenantCode}
          dineHost={dineHost}
          tables={tables}
          onClose={() => setGenerator(false)}
          onAfterCreate={() => void load()}
        />
      )}

      <PrintStyles />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Sub-components                                                      */
/* -------------------------------------------------------------------- */

function QrCard({
  card, size, selected, tenantName, onToggle, onDownload, onRotate, onPreview,
}: {
  card: CardRow;
  size: 'small' | 'medium' | 'large';
  selected: boolean;
  tenantName: string;
  onToggle: () => void;
  onDownload: () => void;
  onRotate: () => void;
  onPreview: () => void;
}) {
  const tiles = {
    small: { qr: 140, pad: 'p-3', code: 'text-[18px]', label: 'text-[10px]' },
    medium: { qr: 200, pad: 'p-4', code: 'text-[28px]', label: 'text-[11px]' },
    large: { qr: 280, pad: 'p-6', code: 'text-[36px]', label: 'text-[12px]' },
  }[size];
  return (
    <Card
      className={`qr-card ${selected ? '' : 'no-print-card opacity-30'} flex flex-col items-center gap-2 ${tiles.pad}`}
    >
      <div className="no-print flex w-full items-center justify-between">
        <label className="inline-flex items-center gap-1.5 text-[11px]">
          <input type="checkbox" checked={selected} onChange={onToggle} />
          Include
        </label>
        <div className="flex gap-1">
          <button type="button" onClick={onPreview} className="rounded p-1 hover:bg-surface-card-hover" title="Preview large">
            <Eye size={12} />
          </button>
          <button type="button" onClick={onDownload} className="rounded p-1 hover:bg-surface-card-hover" title="Download PNG">
            <Download size={12} />
          </button>
          <button type="button" onClick={onRotate} className="rounded p-1 hover:bg-surface-card-hover" title="Rotate QR token (invalidates the old sticker)">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      <div className="text-center">
        <div className={`font-semibold text-content-primary ${tiles.code}`}>{card.code}</div>
        {card.name && card.name !== card.code && (
          <div className="text-[11px] text-content-muted">{card.name}</div>
        )}
      </div>
      <div className="text-center">
        <p className={`uppercase tracking-wider text-content-muted ${tiles.label}`}>
          Scan to order
        </p>
        <p className={`text-content-muted ${tiles.label}`}>
          {tenantName ? `@${tenantName}` : ''}
        </p>
      </div>

      <img
        src={card.pngDataUrl}
        alt={`QR for ${card.code}`}
        width={tiles.qr}
        height={tiles.qr}
        className="rounded-md bg-white"
      />

      <p className="no-print mt-1 break-all text-center text-[9px] text-content-muted">
        {card.url}
      </p>
    </Card>
  );
}

function PreviewModal({ card, onClose }: { card: CardRow; onClose: () => void }) {
  return (
    // Outer overlay uses overflow-y-auto so the entire dialog scrolls
    // on short viewports (phone landscape) instead of clipping.
    <div
      className="no-print fixed inset-0 z-50 overflow-y-auto bg-black/60 p-6"
      onClick={onClose}
    >
      <div className="flex min-h-full items-center justify-center">
        <div
          className="my-auto flex max-h-[90vh] w-full max-w-md flex-col items-center gap-3 overflow-y-auto rounded-lg bg-white p-8 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-3xl font-bold text-gray-900">{card.code}</div>
          <p className="text-xs uppercase tracking-wider text-gray-500">Scan to order</p>
          <img src={card.pngDataUrl} alt="" width={320} height={320} />
          <p className="break-all text-center text-[11px] text-gray-500">{card.url}</p>
          <Button size="sm" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Admin "Generate QR Code" modal.
 *
 *   ┌─────────────────────────────────────────┐
 *   │ Generate QR Code                       × │
 *   │   Tenant:    bill            (locked)   │
 *   │   Branch:    [▾ All branches        ]   │
 *   │   Table:     [▾ Pick a table        ]   │
 *   │   ─ or ─ create a new table inline      │
 *   │   Public host: [https://bill.kr…    ]   │
 *   │                                         │
 *   │   ┌─────── QR PREVIEW ──────┐           │
 *   │   │  ▓▓▓▓▓▓▓▓ … 200×200  ▓▓ │           │
 *   │   └─────────────────────────┘           │
 *   │   URL: https://bill.kr…/dine/bill/t/…   │
 *   │                                         │
 *   │   [Download PNG]  [Open dine link]      │
 *   │   [Close]                               │
 *   └─────────────────────────────────────────┘
 */
function GenerateQrModal({
  tenantCode,
  dineHost,
  tables,
  onClose,
  onAfterCreate,
}: {
  tenantCode: string;
  dineHost: string;
  tables: RestaurantTable[];
  onClose: () => void;
  onAfterCreate: () => void;
}) {
  const tenantId = useSelector((s: { auth: AuthState }) => s.auth?.session?.tenantId ?? '');
  const [branches, setBranches] = useState<Array<{ id: string; code: string; name: string }>>([]);
  const [branchId, setBranchId] = useState<string>('');
  const [tableId, setTableId] = useState<string>('');
  const [newTable, setNewTable] = useState<{ code: string; name: string; seatCount: number; zone: string } | null>(null);
  const [host, setHost] = useState<string>(dineHost);
  const [tenant, setTenant] = useState<string>(tenantCode);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewPng, setPreviewPng] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /* Load branches once */
  useEffect(() => {
    void (async () => {
      try {
        const all = await branchService.all();
        setBranches(
          (all as unknown as Array<Record<string, unknown>>).map((b) => ({
            id: String(b.id ?? b.code ?? ''),
            code: String(b.code ?? ''),
            name: String(b.name ?? b.code ?? ''),
          })),
        );
      } catch {
        /* branches optional */
      }
    })();
  }, []);

  /* Filter tables by branch */
  const tablesInBranch = useMemo(() => {
    if (!branchId) return tables;
    return tables.filter(
      (t) =>
        String((t as { branchId?: unknown }).branchId ?? '') === branchId ||
        String((t as { branchCode?: unknown }).branchCode ?? '') === branchId,
    );
  }, [tables, branchId]);

  /* Live regenerate QR every time inputs change */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const selected = tables.find((t) => String((t as { id?: unknown }).id ?? t.restaurantTableId ?? '') === tableId);
      let token = '';
      if (selected) {
        token =
          selected.qrToken ||
          readTokenFromRemarks(selected.remarks) ||
          '<press Generate to mint a token>';
      } else if (newTable) {
        token = '<press Create + Generate to mint>';
      }
      if (!token) {
        setPreviewUrl('');
        setPreviewPng('');
        return;
      }
      const safeHost = host?.replace(/\/+$/, '') || (typeof window === 'undefined' ? '' : window.location.origin);
      const url = `${safeHost}/dine/${encodeURIComponent(tenant || 'tenant')}/t/${encodeURIComponent(token)}`;
      setPreviewUrl(url);
      // Only render an actual QR for real tokens (not the "<press generate>" placeholder)
      if (/^[0-9a-fA-F-]{6,}$/.test(token)) {
        try {
          const png = await QRCode.toDataURL(url, {
            width: 480,
            errorCorrectionLevel: 'M',
            margin: 1,
          });
          if (!cancelled) setPreviewPng(png);
        } catch {
          /* ignore */
        }
      } else {
        setPreviewPng('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tableId, newTable, host, tenant, tables]);

  async function mintToken() {
    setBusy(true);
    setErr(null);
    try {
      let target: RestaurantTable | undefined;
      if (newTable) {
        // 1. Create the table first
        const created = await restaurantTableService.create([
          {
            code: newTable.code,
            name: newTable.name,
            description: '',
            status: 0,
            remarks: '',
            branchId: branchId || null,
            seatCount: newTable.seatCount,
            zone: newTable.zone,
            tableStatus: 'Available',
          } as never,
        ]);
        target = created[0] as RestaurantTable;
      } else {
        target = tables.find((t) => String((t as { id?: unknown }).id ?? t.restaurantTableId ?? '') === tableId);
      }
      if (!target) {
        setErr('Pick a table or create a new one first.');
        return;
      }
      // 2. Mint a fresh token + write to BOTH qrToken and remarks
      const token = makeUuid();
      const updated = {
        ...target,
        qrToken: token,
        remarks: stampToken(target.remarks, token),
      };
      await restaurantTableService.update([updated]);
      onAfterCreate();
      // Tickle the live regenerate
      setTableId(String((updated as { id?: unknown }).id ?? updated.restaurantTableId ?? ''));
      setNewTable(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not mint QR token.');
    } finally {
      setBusy(false);
    }
  }

  function download() {
    if (!previewPng) return;
    const a = document.createElement('a');
    a.href = previewPng;
    const selected = tables.find((t) => String((t as { id?: unknown }).id ?? t.restaurantTableId ?? '') === tableId);
    a.download = `qr-${selected?.code ?? selected?.name ?? 'table'}.png`;
    a.click();
  }

  return (
    <div className="no-print fixed inset-0 z-50 bg-black/60 p-4" onClick={onClose}>
      <div className="mx-auto my-8 max-w-lg overflow-hidden rounded-lg bg-surface-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-content-primary">Generate QR Code</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-content-muted hover:bg-surface-card-hover">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3 p-4 text-xs">
          {err && <Alert variant="danger">{err}</Alert>}

          <FormRow label="Tenant ID">
            <input
              value={tenantId}
              readOnly
              className="h-8 w-full rounded border border-border bg-surface-muted px-2 text-content-muted"
              title="Locked — comes from your admin session"
            />
          </FormRow>

          <FormRow label="Tenant code">
            <input
              value={tenant}
              onChange={(e) => setTenant(e.target.value.trim())}
              className="h-8 w-full rounded border border-border bg-surface-card px-2"
            />
          </FormRow>

          <FormRow label="Branch">
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="h-8 w-full rounded border border-border bg-surface-card px-2"
            >
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.code ? `(${b.code})` : ''}
                </option>
              ))}
            </select>
          </FormRow>

          <FormRow label="Table">
            <select
              value={tableId}
              onChange={(e) => {
                setTableId(e.target.value);
                setNewTable(null);
              }}
              className="h-8 w-full rounded border border-border bg-surface-card px-2"
            >
              <option value="">— Select a table —</option>
              {tablesInBranch.map((t) => {
                const row = t as unknown as Record<string, unknown>;
                const id = String(row.id ?? row.restaurantTableId ?? '');
                const zone = String(row.zone ?? '');
                const seats = Number(row.seatCount ?? 0);
                return (
                  <option key={id} value={id}>
                    {t.code ?? t.name ?? `Table ${id}`}
                    {zone ? ` · ${zone}` : ''}
                    {seats ? ` · ${seats} seats` : ''}
                  </option>
                );
              })}
            </select>
          </FormRow>

          <button
            type="button"
            onClick={() => {
              if (newTable) setNewTable(null);
              else {
                setTableId('');
                setNewTable({ code: '', name: '', seatCount: 4, zone: '' });
              }
            }}
            className="text-orange-500 underline"
          >
            {newTable ? '↩ Pick from existing instead' : '+ Or create a new table'}
          </button>

          {newTable && (
            <div className="space-y-2 rounded-md border border-border bg-surface-muted p-2">
              <FormRow label="Code">
                <input
                  value={newTable.code}
                  onChange={(e) => setNewTable({ ...newTable, code: e.target.value })}
                  placeholder="T4"
                  className="h-8 w-full rounded border border-border bg-surface-card px-2"
                />
              </FormRow>
              <FormRow label="Name">
                <input
                  value={newTable.name}
                  onChange={(e) => setNewTable({ ...newTable, name: e.target.value })}
                  placeholder="Table 4"
                  className="h-8 w-full rounded border border-border bg-surface-card px-2"
                />
              </FormRow>
              <FormRow label="Seats">
                <input
                  type="number"
                  value={newTable.seatCount}
                  onChange={(e) => setNewTable({ ...newTable, seatCount: +e.target.value || 0 })}
                  className="h-8 w-24 rounded border border-border bg-surface-card px-2"
                />
              </FormRow>
              <FormRow label="Zone">
                <input
                  value={newTable.zone}
                  onChange={(e) => setNewTable({ ...newTable, zone: e.target.value })}
                  placeholder="Garden · AC · VIP"
                  className="h-8 w-full rounded border border-border bg-surface-card px-2"
                />
              </FormRow>
            </div>
          )}

          <FormRow label="Public host">
            <input
              value={host}
              onChange={(e) => setHost(e.target.value.trim())}
              className="h-8 w-full rounded border border-border bg-surface-card px-2"
            />
          </FormRow>

          {/* QR preview */}
          <div className="mt-2 grid place-items-center rounded-md border border-dashed border-border bg-white p-4">
            {previewPng ? (
              <img src={previewPng} alt="QR preview" width={240} height={240} className="rounded" />
            ) : (
              <div className="grid h-[240px] w-[240px] place-items-center text-center text-[11px] text-content-muted">
                <div>
                  <QrIcon size={36} className="mx-auto mb-2 text-content-muted" />
                  Pick a table (or create one) and click
                  <br />
                  <b>Mint Token + Generate</b>.
                </div>
              </div>
            )}
          </div>
          {previewUrl && (
            <div className="break-all rounded-md bg-surface-muted px-2 py-1.5 text-[10px] text-content-muted">
              {previewUrl}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              size="sm"
              onClick={() => void mintToken()}
              disabled={busy || (!tableId && !newTable)}
            >
              {busy ? 'Working…' : newTable ? 'Create + Generate' : 'Mint Token + Generate'}
            </Button>
            <Button size="sm" variant="outline" onClick={download} disabled={!previewPng}>
              <Download size={14} className="mr-1" /> Download PNG
            </Button>
            {previewUrl && /^[0-9a-fA-F-]{6,}$/.test(previewUrl.split('/').pop() ?? '') && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-surface-card px-3 text-xs font-medium text-content-primary hover:bg-surface-card-hover"
              >
                <ExternalLink size={12} /> Open dine link
              </a>
            )}
            <Button size="sm" variant="ghost" onClick={onClose} className="ml-auto">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-wrap items-center gap-2">
      <span className="w-24 text-content-secondary">{label}</span>
      <div className="flex-1 min-w-[160px]">{children}</div>
    </label>
  );
}

function PrintStyles() {
  return (
    <style>{`
      @media print {
        @page { size: A4; margin: 8mm; }
        body { background: #fff !important; }
        .no-print, .no-print * { display: none !important; }
        .no-print-card { display: none !important; }
        .qr-card { break-inside: avoid; border: 1px dashed #cbd5e1 !important; box-shadow: none !important; }
      }
    `}</style>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function gridClass(size: 'small' | 'medium' | 'large'): string {
  if (size === 'small') return 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3';
  if (size === 'large') return 'grid grid-cols-1 sm:grid-cols-2 gap-6';
  return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';
}

function buildDineUrl(host: string, tenantCode: string, qrToken: string): string {
  const safeHost = host?.replace(/\/+$/, '') || (typeof window === 'undefined' ? '' : window.location.origin);
  const safeTenant = tenantCode || 'tenant';
  return `${safeHost}/dine/${encodeURIComponent(safeTenant)}/t/${encodeURIComponent(qrToken)}`;
}

/**
 * Reads the QR token previously stamped into `RestaurantTable.remarks`.
 * Tokens are written as `qr=<uuid>` (pipe-delimited if other audit
 * entries are already present).
 */
export function readTokenFromRemarks(remarks: string | undefined): string | undefined {
  if (!remarks) return undefined;
  const m = /qr=([0-9a-fA-F-]{6,64})/.exec(remarks);
  return m ? m[1] : undefined;
}

/**
 * Returns a `remarks` string with the `qr=<token>` entry merged in.
 * Replaces any existing `qr=...` entry; preserves anything else
 * already in the audit trail.
 */
export function stampToken(prev: string | undefined, token: string): string {
  const parts = (prev ?? '').split('|').filter(Boolean).filter((p) => !p.startsWith('qr='));
  parts.push(`qr=${token}`);
  return parts.join('|');
}

function makeUuid(): string {
  try {
    // crypto.randomUUID is fine in evergreen browsers + Node 16+.
    return (globalThis.crypto as Crypto | undefined)?.randomUUID?.()
      ?? fallbackUuid();
  } catch {
    return fallbackUuid();
  }
}

function fallbackUuid(): string {
  // RFC4122-ish — good enough for QR tokens.
  const b = new Uint8Array(16);
  (globalThis.crypto as Crypto | undefined)?.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readTenantCode(): string {
  try {
    const raw = sessionStorage.getItem('persist:kss-panel-auth');
    if (raw) {
      const parsed = JSON.parse(raw) as { session?: string };
      const sess = parsed.session ? (JSON.parse(parsed.session) as { tenantCode?: string }) : null;
      if (sess?.tenantCode) return sess.tenantCode;
    }
  } catch { /* fall through */ }
  if (typeof window !== 'undefined') {
    const sub = window.location.hostname.split('.')[0];
    if (sub && sub !== 'www' && sub !== 'localhost') return sub;
  }
  return '';
}

function readDineHost(): string {
  const fromEnv = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_DINE_HOST;
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}
